import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';

import { ApiError, api, formValues, type Holiday } from '../api.js';
import { pageStyles } from '../shared-styles.js';

/** The holiday calendar of one year. dialog.show(year). */
@customElement('holidays-dialog')
export class HolidaysDialog extends LitElement {
  static styles = pageStyles;

  @state() private isOpen = false;
  @state() private year = new Date().getFullYear();
  @state() private holidays: Holiday[] = [];
  @state() private error = '';
  @state() private saving = false;

  async show(year: number) {
    this.year = year;
    this.error = '';
    this.isOpen = true;
    await this.load();
  }

  private async load() {
    try {
      this.holidays = await api.get<Holiday[]>(`/api/holidays?year=${this.year}`);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private onHidden = (e: Event) => {
    if (e.target === e.currentTarget) {
      this.isOpen = false;
      this.dispatchEvent(new CustomEvent('closed', { bubbles: true, composed: true }));
    }
  };

  private async add(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    await this.call(async () => {
      await api.post('/api/holidays', { date: v.date, name: v.name });
      form.reset();
    });
  }

  private async removeHoliday(h: Holiday) {
    if (!confirm(`Remove the holiday on ${h.date}?`)) return;
    await this.call(() => api.delete(`/api/holidays/${h.date}`));
  }

  private async call(fn: () => Promise<unknown>) {
    this.saving = true;
    this.error = '';
    try {
      await fn();
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.error = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.error = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  render() {
    return html`
      <wa-dialog label=${`Holidays ${this.year}`} ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(40rem, calc(100vw - 1.5rem));">
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}
        <p class="muted">Bank holidays entered in Everhour arrive here on import; add the ones Everhour does not know by hand.</p>
        ${this.holidays.length === 0 ? html`<p class="muted">No holidays recorded for ${this.year}.</p>` : nothing}
        ${this.holidays.length
          ? html`
              <div class="table-wrap"><table class="entries">
                <tbody>
                  ${this.holidays.map(
                    (h) => html`
                      <tr>
                        <td>${h.date}</td>
                        <td>${h.name}</td>
                        <td class="muted">${h.source}</td>
                        <td class="actions"><wa-button size="small" appearance="plain" variant="danger" ?disabled=${this.saving} @click=${() => this.removeHoliday(h)}>Remove</wa-button></td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table></div>
            `
          : nothing}
        <form @submit=${this.add}>
          <wa-input name="date" label="Date" type="date" required></wa-input>
          <wa-input name="name" label="Name" required placeholder="Easter Monday"></wa-input>
          <div class="full"><wa-button type="submit" size="small" appearance="outlined" ?loading=${this.saving}>Add holiday</wa-button></div>
        </form>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'holidays-dialog': HolidaysDialog;
  }
}
